import { Card } from '../../../components/ui/card';
import { Skeleton } from '../../../components/ui/skeleton';
import { useDashboardLayout, type WidgetId } from '../../../hooks/useDashboardLayout';
import { cn } from '../../../lib/utils';
import {
  WIDGET_COL_SPAN,
  WIDGET_LABELS,
  WIDGET_ROW_SPAN,
  WIDGET_SIZE_CLASSES,
} from '../dashboardWidgetConfig';

function WidgetSkeleton({ id }: { id: WidgetId }) {
  const isKpi = id.startsWith('kpi-');
  const isTallList = WIDGET_ROW_SPAN[id] === 4;
  const isChart = id === 'revenue-chart';

  if (isKpi) {
    return (
      <Card className="h-full p-5">
        <div className="flex h-full flex-col justify-between">
          <div className="flex items-start justify-between gap-3">
            <div className="space-y-2">
              <Skeleton className="h-3 w-24" />
              <Skeleton className="h-8 w-20" />
            </div>
            <Skeleton className="h-9 w-9 rounded-lg" />
          </div>
          <Skeleton className="h-3 w-32" />
        </div>
      </Card>
    );
  }

  return (
    <Card padding="none" className="h-full overflow-hidden">
      <div className="border-b border-border p-4">
        <div className="flex items-start justify-between gap-4">
          <div className="space-y-2">
            <p className="text-sm font-semibold text-muted-foreground">{WIDGET_LABELS[id]}</p>
            <Skeleton className="h-3 w-44" />
          </div>
          <Skeleton className="h-7 w-20 rounded-md" />
        </div>
      </div>
      <div className="h-full min-h-0 p-4">
        {isChart ? (
          <div className="flex h-full min-h-0 flex-col gap-3">
            <div className="flex items-end gap-2">
              <Skeleton className="h-6 w-24" />
              <Skeleton className="h-4 w-16" />
            </div>
            <Skeleton className="h-[280px] w-full" />
          </div>
        ) : isTallList ? (
          <div className="space-y-3">
            {[0, 1, 2, 3, 4].map((item) => (
              <div key={item} className="flex items-center gap-3 rounded-lg border border-border/70 p-3">
                <Skeleton className="h-10 w-10 rounded-md" />
                <div className="min-w-0 flex-1 space-y-2">
                  <Skeleton className="h-3 w-2/3" />
                  <Skeleton className="h-3 w-1/3" />
                </div>
                <Skeleton className="h-6 w-16 rounded-full" />
              </div>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-3">
            {[0, 1, 2, 3].map((item) => (
              <div key={item} className="rounded-lg bg-muted/30 p-3">
                <Skeleton className="h-3 w-20" />
                <Skeleton className="mt-3 h-7 w-16" />
                <Skeleton className="mt-2 h-3 w-24" />
              </div>
            ))}
          </div>
        )}
      </div>
    </Card>
  );
}

export function DashboardPageSkeleton() {
  const { visibleWidgets } = useDashboardLayout();

  return (
    <div data-testid="dashboard-skeleton" aria-label="Loading dashboard" className="dashboard-shell space-y-4">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <div className="space-y-2">
          <Skeleton className="h-8 w-40" />
          <Skeleton className="h-4 w-80 max-w-full" />
        </div>
        <div className="flex flex-wrap items-center gap-2 lg:justify-end">
          <Skeleton className="h-9 w-48 rounded-md" />
          <Skeleton className="h-9 w-28 rounded-md" />
          <Skeleton className="h-9 w-24 rounded-md" />
        </div>
      </div>

      <div className="dashboard-widget-grid grid grid-cols-12 gap-4 auto-rows-[190px] grid-flow-row-dense">
        {visibleWidgets.map((id) => (
          <div
            key={id}
            data-dashboard-span={WIDGET_COL_SPAN[id]}
            className={cn(
              'min-w-0',
              WIDGET_SIZE_CLASSES[id],
              WIDGET_ROW_SPAN[id] === 2 && 'row-span-2',
              WIDGET_ROW_SPAN[id] === 4 && 'row-span-4'
            )}
          >
            <WidgetSkeleton id={id} />
          </div>
        ))}
      </div>
    </div>
  );
}
